import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareDialog from "@/components/feature/ShareDialog";
import ArticleGridCard from "@/components/feature/ArticleGridCard";
import EmptyState from "@/components/feature/EmptyState";
import { formatFullDate, formatViews, getArticleById, getCategoryById, getRelatedArticles } from "@/utils/news";

export default function DetailPage() {
  const { newsId } = useParams();
  const article = getArticleById(newsId);
  const category = getCategoryById(article?.categoryId);
  const [shareOpen, setShareOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: article ? `${article.title} - 黑料吃瓜网` : "资讯不存在 - 黑料吃瓜网",
    description: article
      ? `${article.summary.slice(0, 50)}`
      : "该资讯已被删除或不存在，可返回首页浏览其他娱乐八卦内容。",
    keywords: article
      ? `黑料吃瓜网,${article.title},${article.tags.join(",")}`
      : "黑料吃瓜网,娱乐八卦",
  });

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [newsId]);

  if (!article) {
    return (
      <div className="w-full max-w-[860px] mx-auto px-4 md:px-6 py-14 md:py-20">
        <EmptyState
          icon="ri-error-warning-line"
          title="该资讯已被删除或不存在"
          description="链接可能已经失效，或者这篇内容已被编辑下架。你可以返回首页查看最新资讯，也可以去分类页继续浏览。"
          actionLabel="浏览娱乐新闻"
          actionTo="/category/entertainment"
        />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  return (
    <>
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full max-w-[860px] mx-auto px-4 md:px-6 pt-5 pb-6 md:pt-6 md:pb-8">
          <Breadcrumb
            items={[
              { label: category?.name ?? "资讯", to: `/category/${article.categoryId}` },
              { label: article.title },
            ]}
          />

          {loading ? (
            <div className="mt-6 space-y-3 animate-pulse">
              <div className="h-4 w-24 bg-background-200 rounded" />
              <div className="h-8 w-full bg-background-200 rounded" />
              <div className="h-8 w-3/4 bg-background-200 rounded" />
              <div className="h-4 w-56 bg-background-200 rounded" />
            </div>
          ) : (
            <>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-[11px] hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {category?.name ?? "资讯"}
                </Link>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-[11px] hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-[22px] leading-snug sm:text-3xl md:text-[38px] md:leading-[1.35] font-bold text-foreground-950">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5 text-foreground-700">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px]">
                    {article.author.slice(0, 1)}
                  </span>
                  {article.author}
                </span>
                <span className="text-background-400">·</span>
                <span>{article.source}</span>
                <span className="text-background-400">·</span>
                <span>{formatFullDate(article.publishedAt)}</span>
                <span className="flex items-center gap-1 ml-auto">
                  <i className="ri-eye-line" />
                  {formatViews(article.views)} 阅读
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <article className="w-full max-w-[860px] mx-auto px-4 md:px-6 py-6 md:py-9">
        {loading ? (
          <div className="space-y-3 animate-pulse">
            <div className="w-full aspect-[16/9] rounded-lg bg-background-200" />
            <div className="pt-4 space-y-2.5">
              {[100, 96, 88, 100, 72].map((width, index) => (
                <div
                  key={index}
                  className="h-4 rounded bg-background-200"
                  style={{ width: `${width}%` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <figure className="w-full">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 黑料吃瓜网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-[11px] text-foreground-500 text-center">
                配图由编辑生成，仅用于示意 · {article.source}
              </figcaption>
            </figure>

            <div className="article-body mt-6 md:mt-8">
              <p className="text-sm md:text-[15px] leading-relaxed text-foreground-700 bg-background-200/50 border-l-2 border-primary-500 px-4 py-3 !mb-7">
                {article.summary}
              </p>
              {article.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-8 pt-5 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">分享到</span>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-wechat-line" />
                  微信
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-weibo-line" />
                  微博
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  更多
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/category/${article.categoryId}`}
                  className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回{category?.name ?? "分类"}
                </Link>
                <span className="text-background-300">|</span>
                <Link
                  to="/"
                  className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line" />
                  首页
                </Link>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200 p-4 md:p-5">
              <p className="text-xs leading-relaxed text-accent-900 flex items-start gap-2">
                <i className="ri-information-line mt-0.5" />
                <span>
                  声明：本站为演示站点，文中人物、事件与数据均为虚构示例，不构成对任何真实个人或机构的描述。请理性阅读，不传播未经核实的信息。
                </span>
              </p>
            </div>
          </>
        )}
      </article>

      <section className="w-full max-w-[1100px] mx-auto px-4 md:px-6 pb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1.5 h-5 rounded-full bg-primary-500" />
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">相关阅读</h2>
          <span className="text-xs text-foreground-500 ml-1">按分类与标签相似度推荐</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {related.map((item) => (
            <ArticleGridCard key={item.id} article={item} />
          ))}
        </div>
      </section>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </>
  );
}